import { useState } from 'react'
import { Check, Pencil, Plus, Trash2, X } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { MAX_HOME_LAYOUTS, canAddHomeLayout, type HomeLayoutPrefs } from '@/lib/homeLayouts'
import { cn } from '@/lib/utils'

const ROW = 'group flex h-8 items-center gap-2 rounded-md px-2 text-sm'
const ICON_BUTTON =
  'flex h-6 w-6 shrink-0 items-center justify-center rounded text-gray-400 hover:bg-white hover:text-gray-700'

/**
 * The Customize pencil in the Home header: a list of layouts to switch
 * between. Edit and delete sit on each row, shown on hover or keyboard focus.
 */
export function HomeLayoutMenu({
  prefs,
  onSelect,
  onCreate,
  onEdit,
  onDelete,
}: {
  prefs: HomeLayoutPrefs
  onSelect: (id: string | null) => void
  onCreate: () => void
  onEdit: (id: string) => void
  onDelete: (id: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [confirmId, setConfirmId] = useState<string | null>(null)
  const canAdd = canAddHomeLayout(prefs)

  const run = (action: () => void) => {
    setOpen(false)
    action()
  }

  const options = [{ id: null as string | null, name: 'Default' }, ...prefs.layouts]

  return (
    <Popover
      open={open}
      onOpenChange={next => {
        setOpen(next)
        if (!next) setConfirmId(null)
      }}
    >
      <PopoverTrigger asChild>
        <button
          type='button'
          aria-label='Customize Home'
          title='Customize Home'
          className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-blue-200 bg-blue-50 text-blue-600 transition-colors hover:bg-blue-100'
        >
          <Pencil className='h-3.5 w-3.5' />
        </button>
      </PopoverTrigger>
      <PopoverContent align='end' className='w-56 p-1'>
        <ul>
          {options.map(option => {
            const selected = option.id === prefs.active
            const id = option.id
            if (id !== null && confirmId === id) {
              return (
                <li key={id} className={cn(ROW, 'bg-red-50')}>
                  <span className='min-w-0 flex-1 truncate text-red-700'>Delete {option.name}?</span>
                  <button
                    type='button'
                    aria-label={`Confirm delete ${option.name}`}
                    onClick={() => run(() => onDelete(id))}
                    className={cn(ICON_BUTTON, 'text-red-600 hover:text-red-700')}
                  >
                    <Check className='h-4 w-4' />
                  </button>
                  <button
                    type='button'
                    aria-label='Keep layout'
                    onClick={() => setConfirmId(null)}
                    className={ICON_BUTTON}
                  >
                    <X className='h-4 w-4' />
                  </button>
                </li>
              )
            }
            return (
              <li key={id ?? 'default'} className={cn(ROW, 'hover:bg-gray-100 [&:has(:focus-visible)]:bg-gray-100')}>
                <button
                  type='button'
                  aria-pressed={selected}
                  onClick={() => run(() => onSelect(id))}
                  className={cn(
                    'flex h-full min-w-0 flex-1 items-center gap-2 text-left focus-visible:outline-none',
                    selected ? 'font-medium text-gray-900' : 'text-gray-600',
                  )}
                >
                  <Check className={cn('h-3.5 w-3.5 shrink-0 text-blue-600', !selected && 'invisible')} />
                  <span className='truncate'>{option.name}</span>
                </button>
                {id !== null && (
                  <div className='flex opacity-0 transition-opacity group-hover:opacity-100 group-[:has(:focus-visible)]:opacity-100'>
                    <button
                      type='button'
                      aria-label={`Edit layout ${option.name}`}
                      title='Edit'
                      onClick={() => run(() => onEdit(id))}
                      className={ICON_BUTTON}
                    >
                      <Pencil className='h-3.5 w-3.5' />
                    </button>
                    <button
                      type='button'
                      aria-label={`Delete layout ${option.name}`}
                      title='Delete'
                      onClick={() => setConfirmId(id)}
                      className={cn(ICON_BUTTON, 'hover:text-red-600')}
                    >
                      <Trash2 className='h-3.5 w-3.5' />
                    </button>
                  </div>
                )}
              </li>
            )
          })}
        </ul>
        <div className='my-1 h-px bg-gray-100' />
        <button
          type='button'
          disabled={!canAdd}
          title={canAdd ? undefined : `You can save up to ${MAX_HOME_LAYOUTS} layouts`}
          onClick={() => run(onCreate)}
          className={cn(ROW, 'w-full text-blue-600 hover:bg-blue-50 disabled:text-gray-300 disabled:hover:bg-transparent')}
        >
          <Plus className='h-3.5 w-3.5' />
          New layout
        </button>
      </PopoverContent>
    </Popover>
  )
}
